import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Joyride, { EVENTS, STATUS, Step, CallBackProps } from "react-joyride";
import { checkAndMarkOnboardingComplete, markTourCompleteOnServer, STEP_KEYS, getSavedStep, saveStep, clearStep, waitForOnboardingSync } from "../../utils/onboarding";
import { useWidth } from "../../hooks/useWidth";
import { useKnowledgeBase, getArticleById } from "../../hooks/useKnowledgeBase";
import { KnowledgeCategoryGrid } from "../../components/psychologist/home/KnowledgeCategoryGrid";
import { KnowledgeCategoryView } from "../../components/psychologist/home/KnowledgeCategoryView";
import { KnowledgeArticleView } from "../../components/psychologist/home/KnowledgeArticleView";
import { KnowledgeSearchBar } from "../../components/psychologist/home/KnowledgeSearchBar";
import { HomeSkeleton } from "../../components/psychologist/home/HomeSkeleton";
import { usePsychologistContext } from "../../contexts/PsychologistContext";
import { activityTracker } from "../../utils/activityTracker";
import { CheckCircle2, Globe, GraduationCap } from "lucide-react";
import { useIsPwaApp } from "../../pwa/PwaModeContext";

type ViewState =
  | { type: "home" }
  | { type: "category"; categoryId: string }
  | { type: "subcategory"; categoryId: string; subcategoryId: string }
  | { type: "article"; articleId: string };

function viewStateFromParams(params: URLSearchParams): ViewState {
  // No valida existencia acá: las categorías se cargan async desde el backend,
  // así que la validación real (¿existe el articleId/categoryId?) ocurre en el
  // render, una vez que useKnowledgeBase terminó de cargar.
  const articleId = params.get("article");
  if (articleId) return { type: "article", articleId };
  const subcategoryId = params.get("subcategory");
  const categoryId = params.get("category");
  if (categoryId && subcategoryId) return { type: "subcategory", categoryId, subcategoryId };
  if (categoryId) return { type: "category", categoryId };
  return { type: "home" };
}

function paramsFromViewState(state: ViewState): Record<string, string> {
  switch (state.type) {
    case "article": return { article: state.articleId };
    case "category": return { category: state.categoryId };
    case "subcategory": return { category: state.categoryId, subcategory: state.subcategoryId };
    default: return {};
  }
}

export const HomeView = () => {
  const { psychologist } = usePsychologistContext();
  const { categories, isLoading: kbLoading } = useKnowledgeBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const esAcuity = searchParams.get("esacuity") === "true";
  const [viewState, setViewState] = useState<ViewState>(() => viewStateFromParams(searchParams));

  const [runNavTour, setRunNavTour] = useState(false);
  const [canSkipNavTour, setCanSkipNavTour] = useState(false);
  const [navIndex, setNavIndex] = useState<number>(() => {
    const saved = getSavedStep(STEP_KEYS.nav);
    return saved >= 0 ? saved : 0;
  });

  const firstName = psychologist?.name;
  // La app mobile no tiene la barra de la web a la que apunta el tour de navegación (#header-tabs…): no se muestra.
  const isPwaApp = useIsPwaApp();

  const navigate = useCallback((state: ViewState) => {
    setViewState(state);
    setSearchParams(paramsFromViewState(state), { replace: true });
  }, [setSearchParams]);

  // Keep view in sync with URL (browser back/forward)
  useEffect(() => {
    const stateFromUrl = viewStateFromParams(searchParams);
    if (JSON.stringify(stateFromUrl) !== JSON.stringify(viewState)) {
      setViewState(stateFromUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [viewState]);

  // Métricas de Actividad: abrir un artículo cuenta UNA vez (clic, link directo ?article= o link desde otra pantalla).
  // Solo si existe: los ids son del contenido, y uno inválido vuelve a la home sin contar. Nunca se manda el id.
  const lastTrackedArticle = useRef<string | null>(null);
  useEffect(() => {
    if (viewState.type !== "article") {
      lastTrackedArticle.current = null;
      return;
    }
    if (kbLoading || lastTrackedArticle.current === viewState.articleId) return;
    if (!getArticleById(categories, viewState.articleId)) return;
    lastTrackedArticle.current = viewState.articleId;
    activityTracker.trackAction("home_article_opened");
  }, [viewState, categories, kbLoading]);

  const handleSelectArticle = (articleId: string) => navigate({ type: "article", articleId });
  const handleSelectCategory = (categoryId: string) => navigate({ type: "category", categoryId });
  const handleSelectSubcategory = (subcategoryId: string) => {
    if (viewState.type === "category")
      navigate({ type: "subcategory", categoryId: viewState.categoryId, subcategoryId });
  };

  const handleBack = () => {
    if (viewState.type === "article") {
      for (const category of categories) {
        if (category.subcategories) {
          for (const sub of category.subcategories) {
            if (sub.articles.some((a) => a.id === viewState.articleId)) {
              navigate({ type: "subcategory", categoryId: category.id, subcategoryId: sub.id });
              return;
            }
          }
        }
        if (category.articles.some((a) => a.id === viewState.articleId)) {
          navigate({ type: "category", categoryId: category.id });
          return;
        }
      }
    }
    if (viewState.type === "subcategory") {
      navigate({ type: "category", categoryId: viewState.categoryId });
      return;
    }
    navigate({ type: "home" });
  };

  // Nav tour
  const { size } = useWidth();
  // <1024px el header desktop oculta #header-tabs (ver MainLayout.tsx) y la
  // navegación pasa a la bottom-nav-mobile — el tour debe apuntar a la que
  // esté realmente visible en cada viewport.
  const isMobileNav = size === "s";
  const navSteps: Step[] = [
    { target: "#header-profile-trigger", content: "Desde aquí accedés a tu perfil.", disableBeacon: true },
    { target: "#header-logout-button", content: "Usá este botón para cerrar sesión de forma segura.", disableBeacon: true },
    {
      target: isMobileNav ? "#header-tabs-mobile" : "#header-tabs",
      content: "Podés navegar por las distintas pantallas desde estas solapas.",
      disableBeacon: true,
      placement: isMobileNav ? "top" : "bottom",
    },
  ];

  type JoyrideTooltipProps = {
    index: number; size: number; step?: { content?: React.ReactNode };
    backProps: Record<string, unknown>; primaryProps: Record<string, unknown>;
    skipProps: Record<string, unknown>; tooltipProps: Record<string, unknown>;
  };

  const NavJoyrideTooltip = ({ index, size, step, backProps, primaryProps, skipProps, tooltipProps }: JoyrideTooltipProps) => {
    const total = Number(size) || 0;
    const current = Number(index) + 1;
    const isLast = current >= total && total > 0;
    return (
      <div {...tooltipProps} style={{ background: "#fff", borderRadius: 8, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", maxWidth: 420, padding: "1rem 1rem 0.75rem 1rem", color: "#111827" }}>
        <div style={{ marginBottom: "0.75rem" }}>{step?.content}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", paddingTop: "0.25rem" }}>
          {index > 0 && <button {...backProps} className="btn-outline" style={{ height: 32 }}>Atrás</button>}
          <button {...primaryProps} className="btn-primary" style={{ height: 32, fontSize: "0.875rem", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 14px", whiteSpace: "nowrap", lineHeight: "32px" }}>
            {isLast ? "Entendido" : `Continuar (Paso ${current} de ${total})`}
          </button>
        </div>
        {canSkipNavTour && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "0.5rem" }}>
            <button {...skipProps} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "0.9rem", fontFamily: "inherit", fontWeight: 700 }}>Saltar</button>
          </div>
        )}
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const NavTooltipAny = NavJoyrideTooltip as any;

  useEffect(() => {
    if (isPwaApp) return;
    let cancelled = false;
    waitForOnboardingSync().then(() => {
      if (cancelled) return;
      try {
        const done = localStorage.getItem("psi-nav-tour-done");
        if (!done) {
          setCanSkipNavTour(false);
          const saved = getSavedStep(STEP_KEYS.nav);
          const startAt = saved >= 0 ? saved : 0;
          setTimeout(() => { if (!cancelled) { setNavIndex(startAt); setRunNavTour(true); } }, 300);
        } else {
          setCanSkipNavTour(true);
        }
      } catch { /* noop */ }
    });
    return () => { cancelled = true; };
  }, [isPwaApp]);

  useEffect(() => {
    if (runNavTour) saveStep(STEP_KEYS.nav, navIndex);
  }, [navIndex, runNavTour]);

  const renderContent = () => {
    if (kbLoading) {
      return <HomeSkeleton />;
    }

    switch (viewState.type) {
      case "home":
        return (
          <div className="space-y-6 pb-8">
            <div className="text-center space-y-3 py-4">
              <div className="mx-auto w-16 h-16 bg-psi-lime/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-psi-lime" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                ¡Bienvenida{firstName ? `, ${firstName}` : ""}! 🎉
              </h1>
              <p className="text-white/65 max-w-md mx-auto">
                Este es tu espacio de consulta con toda la información que necesitás como parte del equipo.
              </p>
            </div>

            <KnowledgeSearchBar categories={categories} onSelectArticle={handleSelectArticle} className="max-w-xl mx-auto" />

            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-white">Explorar por categoría</h2>
              <KnowledgeCategoryGrid categories={categories} onSelectCategory={handleSelectCategory} />
            </div>

            {/* Huso horario — guía interactiva */}
            <div
              className="cursor-pointer rounded-xl border-2 border-psi-lime/30 bg-psi-lime/5 hover:bg-psi-lime/10 transition-colors"
              onClick={() => handleSelectArticle("huso-horario")}
            >
              <div className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-psi-lime/20 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-psi-lime" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white">🌍 Huso horario</p>
                  <p className="text-sm text-white/60">Configurá tu agenda en tu hora local — guía paso a paso.</p>
                </div>
              </div>
            </div>

            {/* Onboarding desde Acuity — solo si ?esacuity=true */}
            {esAcuity && (
              <a
                href="https://migracion.psimammoliti.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-xl border-2 border-psi-lime/30 bg-psi-lime/5 hover:bg-psi-lime/10 transition-colors"
              >
                <div className="p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-psi-lime/20 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-psi-lime" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white">Onboarding desde Acuity</p>
                    <p className="text-sm text-white/60">Accedé al onboarding de migración</p>
                  </div>
                </div>
              </a>
            )}
          </div>
        );

      case "category": {
        const category = categories.find((c) => c.id === viewState.categoryId);
        if (!category) { setViewState({ type: "home" }); return null; }
        return (
          <KnowledgeCategoryView
            category={category}
            onSelectArticle={handleSelectArticle}
            onSelectSubcategory={handleSelectSubcategory}
            onBack={() => navigate({ type: "home" })}
          />
        );
      }

      case "subcategory": {
        const category = categories.find((c) => c.id === viewState.categoryId);
        const subcategory = category?.subcategories?.find((s) => s.id === viewState.subcategoryId);
        if (!category || !subcategory) { setViewState({ type: "home" }); return null; }
        return (
          <KnowledgeCategoryView
            category={category}
            subcategory={subcategory}
            onSelectArticle={handleSelectArticle}
            onBack={() => navigate({ type: "category", categoryId: viewState.categoryId })}
          />
        );
      }

      case "article": {
        const article = getArticleById(categories, viewState.articleId);
        if (!article) { setViewState({ type: "home" }); return null; }
        return <KnowledgeArticleView article={article} onBack={handleBack} />;
      }

      default:
        return null;
    }
  };

  return (
    <>
      <Joyride
        steps={navSteps}
        run={runNavTour}
        stepIndex={navIndex}
        continuous
        showSkipButton={canSkipNavTour}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        scrollOffset={800}
        hideCloseButton
        tooltipComponent={NavTooltipAny}
        styles={{ options: { primaryColor: '#6D57BC', zIndex: 1400 } }}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Continuar', next: 'Continuar', skip: 'Saltar' }}
        callback={(data: CallBackProps) => {
          const { status, type, action } = data as CallBackProps & { action?: string };
          if (type === 'step:after') {
            if (action === 'next') setNavIndex(i => i + 1);
            if (action === 'prev') setNavIndex(i => Math.max(0, i - 1));
          }
          // Red de seguridad: el target objetivo depende del viewport (ver navSteps) y
          // podría dejar de existir si la psicóloga resizea la ventana en medio del tour.
          // Como el tour es controlado (stepIndex numérico), Joyride no avanza solo ante
          // un target ausente — sin este manejo, quedaría trabado sin botón de cierre.
          if (type === EVENTS.TARGET_NOT_FOUND) {
            setRunNavTour(false);
          }
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunNavTour(false);
            clearStep(STEP_KEYS.nav);
            const wasAlreadyDone = localStorage.getItem("psi-nav-tour-done") === "1";
            try { localStorage.setItem("psi-nav-tour-done", "1"); } catch { /* noop */ }
            checkAndMarkOnboardingComplete();
            if (!wasAlreadyDone) markTourCompleteOnServer('psi-nav-tour-done');
          }
        }}
      />
      {/* El fondo violeta de punta a punta lo pinta `<main>` (contentBackground en
          MainLayout, seteado por AppShell para el tab Home) — este div no necesita
          calzar su alto exacto con el de `<main>`, solo da el padding de contenido. */}
      <div className="px-4 sm:px-8 pt-8 pb-16">
        <div className="max-w-3xl mx-auto">{renderContent()}</div>
      </div>
    </>
  );
};
